import { useEffect, useRef } from 'react';
import { useSession } from '@/context/SessionContext';
import { useUserMenu } from '@/context/UserMenuContext';
import { supabase } from '@/lib/supabase/client';

/**
 * Componente que abre automáticamente el sidebar de login cuando el usuario
 * hace scroll más allá del 70% vertical de la página si no está logueado.
 * Registra cada impresión en Supabase.
 */
export const AutoLoginPrompt: React.FC = () => {
  const { session } = useSession();
  const { openSidebar } = useUserMenu();
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    // Si el usuario está logueado, no hacer nada
    if (session) {
      return;
    }
    
    // Si ya se ejecutó, no hacer nada
    if (hasTriggeredRef.current) return;

    const handleScroll = () => {
      // Calcular el porcentaje de scroll vertical
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

      // Si el usuario ha hecho scroll más allá del 70%
      if (scrollPercent > 70) {
        hasTriggeredRef.current = true;
        
        // Registrar impresión en Supabase (without IP geolocation - disabled)
        const recordImpression = async () => {
          try {
            // IP geolocation disabled - only record basic impression data
            await supabase
              .from('login_prompt_impressions')
              .insert({
                user_agent: navigator.userAgent,
                referrer: document.referrer || null,
                ip_address: null,
                city: null,
                country: null,
                latitude: null,
                longitude: null,
              });
          } catch (err) {
            console.error('Error registrando impresión:', err);
          }
        };
        
        recordImpression();
        openSidebar();
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [session, openSidebar]);

  return null;
};

export default AutoLoginPrompt;
